import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { cn } from '@up2d8/ui';

/**
 * De zijbalk van de medewerker op een bureauscherm (replica-spec §4).
 *
 * Dit is een **replica**: elke maat, kleur en SVG-vorm komt letterlijk uit
 * `docs/design/UP2D8-home-referentie.html`. Waar die een waarde geeft die in `packages/ui` een token
 * heeft — elke kleur, elke radius, elke schaduw — staat het token; de guide is uit deze mockup
 * afgeleid, dus ze zijn gelijk. Waar de mockup een maat gebruikt die geen token heeft
 * (`padding: 9px 12px`), staat die als Tailwind-waarde tussen blokhaken. Dat is een bewuste
 * uitzondering op de huisregel, en ze geldt alleen voor spacing in deze replica
 * (`docs/design/deviations.md`).
 *
 * De iconen zijn de Feather-vormen uit de referentie, niet uit `lucide-react`. Lucide is een fork
 * van Feather en de meeste vormen zijn gelijk, maar "de meeste" is in een replica niet genoeg — en
 * een icoon vervangen door een lijkend icoon is precies hoe een kopie langzaam iets anders wordt.
 */

/** Eén navigatie-item, exact de vorm uit de referentie. */
interface DeskNavItem {
  label: string;
  href: string;
  icon: ReactNode;
  /** Een echte teller of niets. Een badge die altijd hetzelfde getal toont, is een leugen. */
  badge?: number | null;
}

const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

export const deskIcons = {
  // Het wissel-icoon uit de mockup van de ploegoverdracht (Stap 39).
  handover: (
    <svg {...ICON_PROPS}>
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  ),
  home: (
    <svg {...ICON_PROPS}>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  bulletins: (
    <svg {...ICON_PROPS}>
      <path d="M4 6h16M4 12h16M4 18h10" />
    </svg>
  ),
  documents: (
    <svg {...ICON_PROPS}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  calendar: (
    <svg {...ICON_PROPS}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  colleagues: (
    <svg {...ICON_PROPS}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  messages: (
    <svg {...ICON_PROPS}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  tasks: (
    <svg {...ICON_PROPS}>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  settings: (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  chevronDown: (
    <svg {...ICON_PROPS} width={14} height={14}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
} as const;

/** De gevulde ster van de supportkaart. */
export function SupportSparkle({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2L14.09 8.26L20.5 8.5L15.5 12.5L17.2 18.8L12 15.2L6.8 18.8L8.5 12.5L3.5 8.5L9.91 8.26L12 2Z"
        fill="currentColor"
      />
    </svg>
  );
}

function NavItem({ item, active }: { item: DeskNavItem; active: boolean }) {
  return (
    <Link
      to={item.href}
      className={cn(
        'relative flex items-center gap-3 rounded-md px-3 py-[9px] font-heading text-body font-medium transition-colors',
        active
          ? 'bg-[rgba(17,197,217,0.15)] text-text-on-dark'
          : 'text-[rgba(255,255,255,0.7)] hover:bg-[rgba(255,255,255,0.06)] hover:text-text-on-dark',
      )}
      aria-current={active ? 'page' : undefined}
    >
      {/* Het streepje loopt van 10px onder de bovenrand tot 10px boven de onderrand (spec §4.3). */}
      {active ? (
        <span
          aria-hidden
          className="absolute bottom-[10px] left-0 top-[10px] w-[3px] rounded-pill bg-brand-cyan"
        />
      ) : null}
      <span aria-hidden className="flex h-5 w-5 shrink-0 items-center justify-center">
        {item.icon}
      </span>
      <span>{item.label}</span>
      {item.badge ? (
        <span className="ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-pill bg-brand-orange px-[5px] text-micro font-semibold text-text-on-dark">
          {item.badge}
        </span>
      ) : null}
    </Link>
  );
}

export interface DeskSidebarProps {
  /** De naam van het bedrijf zoals de klant hem koos (Extra 1). */
  organisation: string;
  /** Twee letters voor de tegel, als er geen logo is. */
  organisationInitials: string;
  /**
   * Het logo van de klant voor een donkere achtergrond (Extra 1).
   *
   * Ontbreekt het, dan staat er een tegel met de initialen - en niet ons schild: een medewerker
   * die hier het merk van UP2D8 ziet, denkt dat hij in de app van een leverancier zit.
   */
  logoUrl?: string | null;
  main: DeskNavItem[];
  personal: DeskNavItem[];
  /** De `href` van het actieve item, of `null` als geen enkel item bij dit scherm hoort. */
  activeKey: string | null;
  /** Waar "Contact Support" heen gaat; ontbreekt het, dan is er geen knop. */
  supportHref?: string | null;
  /** Een omhulling rond logo en naam: het menu om van bedrijf te wisselen (ACC-1). */
  wrapBrand?: (brand: ReactNode) => ReactNode;
}

export function DeskSidebar({
  organisation,
  organisationInitials,
  logoUrl,
  main,
  personal,
  activeKey,
  supportHref,
  wrapBrand,
}: DeskSidebarProps) {
  const brand = (
    <span className="flex min-w-0 items-center gap-2.5">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={organisation}
          data-testid="desk-logo"
          className="h-[34px] w-[34px] shrink-0 rounded-sm object-contain"
        />
      ) : (
        <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-sm bg-brand-cyan font-heading text-content font-bold text-brand-dark-blue">
          {organisationInitials}
        </span>
      )}
      <span
        data-testid="desk-organisation"
        className="min-w-0 truncate font-heading text-[17px] font-bold tracking-[-0.02em]"
      >
        {organisation}
      </span>
    </span>
  );

  return (
    <aside
      data-testid="desk-sidebar"
      className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col gap-5 overflow-y-auto bg-brand-sidebar px-[14px] py-[18px] text-text-on-dark desk:flex"
    >
      {/*
        Het merk van de klant, niet het onze (Extra 1, ADR-0071).

        De spec zet hier het woordmerk "UP2D8" en daaronder een organisatiewisselaar met dezelfde
        naam erin. Sinds Extra 1 draagt de medewerkerszone het merk van de klant, en dan zou dat
        twee keer dezelfde naam zijn, met die van UP2D8 bovenaan. De wisselaar zit daarom ín het
        merk: wie in meer dan één bedrijf zit, klikt op logo en naam en kiest (ACC-1).
      */}
      <div
        data-testid="desk-brand"
        className="flex items-center border-b border-[rgba(255,255,255,0.1)] px-2 pb-[18px] pt-1"
      >
        {wrapBrand ? wrapBrand(brand) : brand}
      </div>

      <nav aria-label="Hoofdnavigatie" data-testid="desk-nav" className="flex flex-col gap-0.5">
        <p className="px-3 pb-1.5 pt-3 font-heading text-micro font-medium uppercase tracking-[0.1em] text-[rgba(255,255,255,0.4)]">
          Hoofdmenu
        </p>
        {main.map((item) => (
          <NavItem key={item.href} item={item} active={item.href === activeKey} />
        ))}

        <p className="px-3 pb-1.5 pt-3 font-heading text-micro font-medium uppercase tracking-[0.1em] text-[rgba(255,255,255,0.4)]">
          Persoonlijk
        </p>
        {personal.map((item) => (
          <NavItem key={item.href} item={item} active={item.href === activeKey} />
        ))}
      </nav>

      {supportHref ? (
        /*
          De supportkaart. `overflow-hidden` snijdt de bovenste 12px van de sterbadge weg; dat staat
          zo in de mockup en de spec zegt uitdrukkelijk het zo over te nemen (§4.4).
        */
        <div className="relative mt-auto overflow-hidden rounded-lg bg-gradient-to-br from-brand-primary to-brand-cyan px-[14px] pb-[14px] pt-[18px] text-center">
          <span
            aria-hidden
            className="absolute -right-[30px] -top-[30px] h-[100px] w-[100px] rounded-full bg-[rgba(255,255,255,0.08)]"
          />
          <span
            aria-hidden
            className="absolute -bottom-10 -left-5 h-[120px] w-[120px] rounded-full bg-[rgba(255,255,255,0.06)]"
          />
          <span className="absolute -top-3 left-1/2 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-md bg-brand-orange text-text-on-dark shadow-[0_4px_12px_rgba(233,141,78,0.4)]">
            <SupportSparkle />
          </span>
          <p className="relative mb-0.5 mt-[18px] font-heading text-body font-semibold text-text-on-dark">
            Hulp nodig?
          </p>
          <p className="relative mb-2.5 text-micro leading-[1.4] text-[rgba(255,255,255,0.85)]">
            Ons support team helpt je graag verder
          </p>
          <a
            href={supportHref}
            className="relative block w-full rounded-pill bg-surface px-[14px] py-[7px] font-heading text-caption font-semibold text-brand-primary"
          >
            Contact Support
          </a>
        </div>
      ) : null}
    </aside>
  );
}
